(function () {
    const getJquery = () => window.jQuery || window.$ || null;

    const debugLog = (...args) => {
        window.taskello?.debug?.log?.(...args);
    };

    const cleanupModalArtifacts = () => {
        document.querySelectorAll('[data-modal-backdrop], .modal-backdrop').forEach((backdrop) => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
    };

    class ModalBridge {

        constructor() {
            this.observer = null;
            this.ajaxModalPatched = false;
            this.init();
        }

        hasAjaxModal() {
            const $ = getJquery();
            return Boolean($?.fn?.ajaxmodal);
        }

        patchAjaxModal() {
            const $ = getJquery();
            if (!$?.fn?.ajaxmodal || this.ajaxModalPatched) {
                return;
            }

            const originalAjaxModal = $.fn.ajaxmodal;
            const bridge = this;

            $.fn.ajaxmodal = function (...args) {
                const $element = $(this);
                const action = typeof args[0] === 'string' ? args[0] : 'create';
                const options = typeof args[0] === 'string' ? (args[1] ?? {}) : (args[0] ?? {});

                if (action === 'create') {
                    const observer = new MutationObserver((mutations) => {
                        let $createdModal = null;

                        mutations.forEach((mutation) => {
                            if ($createdModal?.length || mutation.type !== 'childList') {
                                return;
                            }

                            mutation.addedNodes.forEach((node) => {
                                if ($createdModal?.length || node.nodeType !== Node.ELEMENT_NODE) {
                                    return;
                                }

                                const $node = $(node);

                                if ($node.is('[data-modal-root], .modal')) {
                                    $createdModal = $node;
                                    return;
                                }

                                const $nestedModal = $node.find('[data-modal-root], .modal').first();
                                if ($nestedModal.length) {
                                    $createdModal = $nestedModal;
                                }
                            });
                        });

                        if ($createdModal?.length) {
                            observer.disconnect();
                            $(document).trigger('ajaxmodal.created', [{
                                trigger: $element,
                                modal: $createdModal,
                                action,
                                options
                            }]);
                        }
                    });

                    observer.observe(document.body, {
                        childList: true,
                        subtree: true
                    });

                    window.setTimeout(() => observer.disconnect(), 5000);

                    $element.off('modallink.success.taskello-global');
                    $element.one('modallink.success.taskello-global', (event, response, modal, preventDefaultCallback) => {
                        $(document).trigger('ajaxmodal.success', [{
                            trigger: $element,
                            response,
                            modal,
                            preventDefaultCallback,
                            action,
                            options
                        }]);
                    });
                }

                const result = originalAjaxModal.apply(this, args);
                bridge.modalInitLinks();

                return result;
            };

            this.ajaxModalPatched = true;
        }

        modalInitLinks(container = document) {
            debugLog('modalInitLinks', container);

            const $ = getJquery();

            if (!$ || !this.hasAjaxModal()) {
                return;
            }

            $(container).find('.modallink').each(function () {
                const $modalLink = $(this);

                if ($modalLink.data('modallink-initialized')) {
                    return;
                }

                $modalLink.data('modallink-initialized', true);

                $modalLink.on('modallink.success', function (event, response, modal, preventDefaultCallback) {
                    const parsedResponse = window.dynamic?.tryParseJson?.(response);
                    const isJsonResponse = parsedResponse !== null && typeof parsedResponse === 'object';

                    if (!isJsonResponse) {
                        return;
                    }

                    if (typeof preventDefaultCallback === 'function') {
                        preventDefaultCallback();
                    }

                    const responseForProcessing = {
                        ...parsedResponse,
                        modalDestroy: parsedResponse.modalDestroy ?? true
                    };

                    window.taskello?.uiAjaxResponseProcess?.(responseForProcessing, $modalLink, {
                        allowDefaultReload: true
                    });
                });
            });
        }

        modalObserver() {
            debugLog('modalObserver');

            if (this.observer || !this.hasAjaxModal()) {
                return;
            }

            this.observer = new MutationObserver((mutations) => {
                let shouldInit = false;

                mutations.forEach((mutation) => {
                    if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
                        return;
                    }

                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            shouldInit = true;
                        }
                    });
                });

                if (shouldInit) {
                    window.setTimeout(() => {
                        this.modalInitLinks();
                    }, 100);
                }
            });

            this.observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }

        modalEvent() {
            debugLog('modalEvent');

            const $ = getJquery();
            if (!$ || !this.hasAjaxModal()) {
                return;
            }

            this.patchAjaxModal();
            this.modalInitLinks();

            if (!$(document).data('taskello-ajaxmodal-created-bound')) {
                $(document).data('taskello-ajaxmodal-created-bound', true);
                $(document).on('ajaxmodal.created', (event, payload = {}) => {
                    const container = payload?.modal?.[0] || payload?.modal || document;

                    window.setTimeout(() => {
                        this.modalInitLinks(container);
                    }, 50);
                });
            }

            if (!$(document).data('taskello-ajaxmodal-success-bound')) {
                $(document).data('taskello-ajaxmodal-success-bound', true);
                $(document).on('ajaxmodal.success', (event, payload = {}) => {
                    const parsedResponse = window.dynamic?.tryParseJson?.(payload.response);
                    const isJsonResponse = parsedResponse !== null && typeof parsedResponse === 'object';

                    if (!isJsonResponse) {
                        return;
                    }

                    if (payload.trigger?.hasClass?.('modallink')) {
                        return;
                    }

                    if (typeof payload.preventDefaultCallback === 'function') {
                        payload.preventDefaultCallback();
                    }

                    const responseForProcessing = {
                        ...parsedResponse,
                        modalDestroy: parsedResponse.modalDestroy ?? true
                    };

                    window.taskello?.uiAjaxResponseProcess?.(responseForProcessing, payload.trigger || $(document), {
                        allowDefaultReload: true
                    });
                });
            }

            if (!$(document).data('taskello-modal-init-click-bound')) {
                $(document).data('taskello-modal-init-click-bound', true);
                $(document).on('click', '.modallink', () => {
                    window.setTimeout(() => {
                        this.modalInitLinks();
                    }, 500);
                });
            }
        }

        init() {
            const run = () => {
                this.modalEvent();
                this.modalObserver();
            };

            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }
    }

    window.taskello = window.taskello || {};

    const modalBridge = new ModalBridge();

    window.taskello.cleanupModalArtifacts = cleanupModalArtifacts;
    window.taskello.modalInitLinks = (container = document) => modalBridge.modalInitLinks(container);
    window.taskello.modalObserver = () => modalBridge.modalObserver();
    window.taskello.modalEvent = () => modalBridge.modalEvent();
})();
